import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { deleteClient, fetchClient, updateClient } from '@/features/clients/api'
import type { ApiError } from '@/lib/api/client'
import type { Client, ClientHealth } from '@/types/support'

const HEALTH: ClientHealth[] = ['good', 'watch', 'at_risk']

interface ClientForm {
  name: string
  sector: string
  primaryContactName: string
  primaryContactEmail: string
  health: ClientHealth
  healthReason: string
  notes: string
}

/**
 * A client, opened and amendable.
 *
 * Health is the field that earns the dialog. It drives the "needs attention"
 * count on the portfolio, and a status nobody can change is a status that stops
 * being true — so the reason sits next to it, because "at risk" without a
 * sentence is an argument waiting to happen.
 */
export function ClientPanel({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ClientForm | null>(null)

  const client = useQuery<Client, ApiError>({
    queryKey: ['client', slug],
    queryFn: () => fetchClient(slug),
    retry: false,
  })

  // Derived, not copied in an effect: the form starts from the server's answer
  // and only exists once the reader has typed something.
  const data = client.data
  const values: ClientForm | null = form ?? (data === undefined ? null : {
    name: data.name,
    sector: data.sector ?? '',
    primaryContactName: data.primaryContact.name ?? '',
    primaryContactEmail: data.primaryContact.email ?? '',
    health: data.health,
    healthReason: data.healthReason ?? '',
    notes: data.notes ?? '',
  })

  const set = (key: keyof ClientForm) => (value: string) => {
    if (values !== null) setForm({ ...values, [key]: value })
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['clients'] })
    void queryClient.invalidateQueries({ queryKey: ['client', slug] })
    void queryClient.invalidateQueries({ queryKey: ['portfolio'] })
  }

  const blank = (v: string): string | null => (v.trim() === '' ? null : v.trim())

  const save = useMutation<Client, ApiError, void>({
    mutationFn: () =>
      updateClient(slug, {
        name: (values?.name ?? '').trim(),
        sector: blank(values?.sector ?? ''),
        primaryContactName: blank(values?.primaryContactName ?? ''),
        primaryContactEmail: blank(values?.primaryContactEmail ?? ''),
        health: values?.health ?? 'good',
        healthReason: blank(values?.healthReason ?? ''),
        notes: blank(values?.notes ?? ''),
      }),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteClient(slug),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const error = save.error ?? remove.error ?? client.error

  return (
    <Dialog title={data?.name ?? t('common.loading')} onClose={onClose}>
      {values === null ? (
        <p className="text-ink-500 px-5 py-4 font-mono text-[11px]">{t('common.loading')}</p>
      ) : (
        <form
          className="flex max-h-[70vh] flex-col gap-3.5 overflow-y-auto px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (values.name.trim() !== '') save.mutate()
          }}
        >
          <Field label={t('clients.name')}>
            <input value={values.name} onChange={(e) => set('name')(e.target.value)} className={FIELD_CLASS} />
          </Field>

          <div className="grid grid-cols-2 gap-3.5">
            <Field label={t('clients.sector')}>
              <input value={values.sector} onChange={(e) => set('sector')(e.target.value)} className={FIELD_CLASS} />
            </Field>
            <Field label={t('clients.health')}>
              <select value={values.health} onChange={(e) => set('health')(e.target.value)} className={FIELD_CLASS}>
                {HEALTH.map((h) => (
                  <option key={h} value={h}>
                    {t(h === 'good' ? 'clients.healthGood' : h === 'watch' ? 'clients.healthWatch' : 'clients.healthAtRisk')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('clients.contactName')}>
              <input value={values.primaryContactName} onChange={(e) => set('primaryContactName')(e.target.value)} className={FIELD_CLASS} />
            </Field>
            <Field label={t('clients.contactEmail')}>
              <input type="email" value={values.primaryContactEmail} onChange={(e) => set('primaryContactEmail')(e.target.value)} className={FIELD_CLASS} />
            </Field>
          </div>

          <Field label={t('clients.healthReason')}>
            <input value={values.healthReason} onChange={(e) => set('healthReason')(e.target.value)} className={FIELD_CLASS} />
          </Field>

          <Field label={t('clients.notes')}>
            <textarea value={values.notes} rows={3} onChange={(e) => set('notes')(e.target.value)} className={`${FIELD_CLASS} resize-none`} />
          </Field>

          {data?.projects === undefined || data.projects.length === 0 ? null : (
            <div className="flex flex-col gap-1">
              <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
                {t('clients.projectsHere')}
              </span>
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {data.projects.map((project) => (
                  <li key={project.id}>
                    <Link to={`/projects/${project.key}`} className="text-brand-600 font-mono text-[11px]">
                      {project.key}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error === null || error === undefined ? null : (
            <p className="text-[12px] text-[color:var(--color-state-blocked)]">{error.message}</p>
          )}

          <div className="border-line flex items-center gap-3 border-t pt-3.5">
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('clients.deleteConfirm'))) remove.mutate()
              }}
              className="text-ink-500 hover:text-[color:var(--color-state-blocked)] text-[12px] underline"
            >
              {t('clients.delete')}
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="bg-brand-600 ms-auto rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
            >
              {save.isPending ? t('clients.saving') : t('clients.save')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
